import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import LookupSelect from '../components/LookupSelect';
import { lookupBookableProduct, lookupFacility } from '../lookups';

/**
 * Start a crawling by hand.
 *
 * The old page picks a facility and then a product inside it, which is how
 * anybody thinks about this — you know the venue, not the product's GUID. Both
 * are lookups here, and the facility one only narrows the search: the endpoint
 * takes a product.
 *
 * `FirstDay` and `DaysToCrawl` are offsets in days from today, not dates. The
 * old form labels them "First Day" and "Days To Crawl" and leaves you to work
 * that out; the hint here says what 0 and 1 mean, because getting it wrong
 * crawls the wrong week.
 *
 * The result is a queued run, not a finished one: the page says so and points at
 * the list rather than pretending to report activities that do not exist yet.
 */

const CrawlingRunInternal = ({ api }) => {
  const [facilityId, setFacilityId] = useState('');
  const [productId, setProductId] = useState('');
  const [firstDay, setFirstDay] = useState('0');
  const [daysToCrawl, setDaysToCrawl] = useState('1');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const numeric = value => value !== '' && Number.isInteger(Number(value)) && Number(value) >= 0;
  const canSubmit = !!productId && numeric(firstDay) && numeric(daysToCrawl) && !busy;

  const submit = async event => {
    event.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setNote(null);
    try {
      await api.crawlings.crawlProduct({
        filters: {
          productId,
          firstDay: Number(firstDay),
          daysToCrawl: Number(daysToCrawl)
        }
      });
      setNote({
        tone: 'info',
        text: 'Queued. It appears in the crawlings list once it runs.'
      });
    } catch (error) {
      setNote({
        tone: 'error',
        text: error?.response?.data?.message || error?.message || 'Unable to start the crawling.'
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="next-page">
      <BackToList to="/next/crawlings">← All crawlings</BackToList>

      <header className="next-page__header">
        <h1>Run a crawling</h1>
        <p className="next-muted">Fetches a product’s availability from the venue’s system now.</p>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          {note.tone === 'info' && <> <Link className="next-link" to="/next/crawlings">Open the list</Link>.</>}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <section className="next-card">
        <form className="next-form__grid" onSubmit={submit}>
          {/* Narrows the product search; the endpoint itself takes a product. */}
          <LookupSelect
            label="Facility"
            value={facilityId}
            onChange={setFacilityId}
            loadOptions={key => lookupFacility(api, key)}
            placeholder="Name or id — optional"
          />

          <LookupSelect
            label="Product"
            value={productId}
            onChange={setProductId}
            loadOptions={key => lookupBookableProduct(api, key)}
            placeholder="Name or id"
          />

          <label className="next-filter">
            <span className="next-filter__label">First day</span>
            <input
              className="next-input"
              value={firstDay}
              onChange={event => setFirstDay(event.target.value)}
            />
            <span className={numeric(firstDay) ? 'next-form__hint' : 'next-form__error'}>
              {numeric(firstDay)
                ? 'Days from today. 0 starts with today.'
                : 'A whole number of days, 0 or more.'}
            </span>
          </label>

          <label className="next-filter">
            <span className="next-filter__label">Days to crawl</span>
            <input
              className="next-input"
              value={daysToCrawl}
              onChange={event => setDaysToCrawl(event.target.value)}
            />
            <span className={numeric(daysToCrawl) ? 'next-form__hint' : 'next-form__error'}>
              {numeric(daysToCrawl)
                ? 'How many days forward, starting from the first day.'
                : 'A whole number of days, 0 or more.'}
            </span>
          </label>
        </form>

        <div className="next-form__actions">
          <button
            type="button"
            className="next-btn next-btn--primary"
            disabled={!canSubmit}
            title={productId ? undefined : 'Pick a product first'}
            onClick={submit}
          >
            {busy ? 'Starting…' : 'Start crawling'}
          </button>
          <Link className="next-btn" to="/next/crawlings">Cancel</Link>
        </div>
      </section>
    </div>
  );
};

CrawlingRunInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const CrawlingRun = withApi(CrawlingRunInternal);

CrawlingRun.requiresLogin = true;

export default CrawlingRun;
